import { appEnv } from "../../config/env";

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
}

export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  timeoutMs?: number;
  authenticated?: boolean;
}

let accessTokenProvider: () => string | null | Promise<string | null> = () => null;

export const registerAccessTokenProvider = (
  provider: () => string | null | Promise<string | null>,
) => {
  accessTokenProvider = provider;
};

const createRequestId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return response.json();
  const text = await response.text();
  return text || undefined;
};

export class HttpClient {
  constructor(
    private readonly baseUrl: string,
    private readonly defaultTimeoutMs = 12_000,
  ) {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const controller = new AbortController();
    const requestId = createRequestId();
    const timeout = globalThis.setTimeout(
      () => controller.abort(),
      options.timeoutMs ?? this.defaultTimeoutMs,
    );
    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    headers.set("X-Request-ID", requestId);
    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (options.authenticated !== false) {
      const token = await accessTokenProvider();
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }

    try {
      const normalizedBase = this.baseUrl.endsWith("/")
        ? this.baseUrl
        : `${this.baseUrl}/`;
      const response = await fetch(
        new URL(path.replace(/^\/+/, ""), normalizedBase),
        {
          ...options,
          headers,
          body:
            options.body === undefined
              ? undefined
              : JSON.stringify(options.body),
          signal: controller.signal,
        },
      );
      const body = await readBody(response);
      if (!response.ok) {
        const error =
          typeof body === "object" && body !== null
            ? (body as ApiErrorBody)
            : undefined;
        throw new HttpError(
          error?.message ?? "Não foi possível concluir a solicitação.",
          response.status,
          error?.code ?? "HTTP_ERROR",
          response.headers.get("x-request-id") ?? requestId,
          error?.details,
        );
      }
      return body as T;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new HttpError(
          "A solicitação excedeu o tempo limite.",
          408,
          "REQUEST_TIMEOUT",
          requestId,
        );
      }
      throw new HttpError(
        "Não foi possível conectar ao servidor.",
        0,
        "NETWORK_ERROR",
        requestId,
      );
    } finally {
      globalThis.clearTimeout(timeout);
    }
  }

  get<T>(path: string, options?: RequestOptions) {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  delete<T>(path: string, options?: RequestOptions) {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }
}

export const apiClient = new HttpClient(
  appEnv.apiUrl ?? "http://127.0.0.1:3000/api/v1/",
);
